// 更換 Wi-Fi（重配，模式 A；device/09 §3、§5.4；owner-only）。
// webapp 只支援模式 A（線上雲端命令，走 WSS opSetRaw）——模式 B(BLE)/C(SoftAP) 為原生 App。
// 流程：idle → scanning(WifiScan) → picking(選 SSID+psk) → switching(WifiReconfigure) →
//        done/failed。★ 送出的 ok ≠ 切換成功；結果等 wifi.reconfig.* 事件（§3.2），逾時 fallback。

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../../lib/ws';
import type { SignalFrame } from '../../../lib/ws';
import type { DeviceSummary } from '../deviceModel';
import { decideForDevice } from './reconfigDecision';
import { WIFI_EVT_FAILED, WIFI_EVT_SUCCEEDED, WIFI_SSID } from './ids';
import { frameValue, opWifiReconfigure, opWifiScan, useAttr, type WifiAp } from './ops';
import { SectionCard } from './widgets';

type Phase = 'idle' | 'scanning' | 'picking' | 'switching' | 'done' | 'failed';

const RESULT_TIMEOUT_MS = 120_000; // 等切換結果事件的上限（T_rollback 90s + 緩衝）

// device/09 §3 模式選擇：webapp 只做模式 A(線上雲端命令)。模式 B(BLE)/C(SoftAP) 需就近無線電,
// 瀏覽器無此能力(foundation/22、foundation/00)→ 非 'cloud'(裝置離線)時不顯示換網流程,改提示改用手機 App。
export function WifiReconfigSection({ device, wsOpen }: { device: DeviceSummary; wsOpen: boolean }) {
  const { t } = useTranslation();
  const deviceId = device.device_id;
  const mode = decideForDevice(device); // 'cloud'(在線)才走模式 A;其餘(離線 ble/softAp/lastResort)= 本機/手動,web 不適用
  const localOnly = mode !== 'cloud';
  const currentSsid = useAttr(deviceId, WIFI_SSID, wsOpen && !localOnly); // STAT：目前連線 SSID（唯讀）
  const [phase, setPhase] = useState<Phase>('idle');
  const [aps, setAps] = useState<WifiAp[]>([]);
  const [ssid, setSsid] = useState('');
  const [psk, setPsk] = useState('');
  const [error, setError] = useState<string | null>(null);
  const target = useRef('');

  const startScan = useCallback(async () => {
    setError(null);
    setPhase('scanning');
    try {
      const found = await opWifiScan(deviceId);
      setAps(found);
      // 預設選「目前連線」那個(在掃描清單裡才選),對齊 iOS/Android。
      const cur = currentSsid.value;
      if (typeof cur === 'string' && cur && found.some((a) => a.ssid === cur)) setSsid(cur);
      setPhase('picking');
    } catch {
      setError(t('deviceSettings.wifiReconfigScanFailed'));
      setPhase('idle');
    }
  }, [deviceId, t, currentSsid]);

  const submit = useCallback(async () => {
    if (!ssid) return;
    target.current = ssid;
    setError(null);
    setPhase('switching');
    try {
      await opWifiReconfigure(deviceId, { ssid, psk: psk || undefined });
      setPsk(''); // 送出即清 psk（勿滯留）；ok=受理，非成功，等事件
    } catch {
      setError(t('deviceSettings.wifiReconfigSendFailed'));
      setPhase('picking');
    }
  }, [deviceId, ssid, psk, t]);

  // 監看結果事件（device/09 §3.2：wifi.reconfig.succeeded/failed），逾時視為未確認失敗。
  useEffect(() => {
    if (phase !== 'switching') return;
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId) return;
      const p = (frame.payload ?? frame) as Record<string, unknown>;
      const id = String(frame.id ?? p.id ?? '').toUpperCase();
      const name = String(p.event_type ?? p.name ?? '');
      if (id === WIFI_EVT_SUCCEEDED.toUpperCase() || name === 'wifi.reconfig.succeeded') {
        setPhase('done');
      } else if (id === WIFI_EVT_FAILED.toUpperCase() || name === 'wifi.reconfig.failed') {
        const reason = typeof p.reason === 'string' ? p.reason : '';
        setError(reason ? t('deviceSettings.wifiReconfigFailedReason', { reason }) : t('deviceSettings.wifiReconfigFailed'));
        setPhase('failed');
      } else if (frame.type === 'report' && id === WIFI_SSID.toUpperCase()) {
        // 換網完成後 device 主動送 WifiSSID 屬性變更 report(device/09 §3.2 D0-7 輪詢 WifiSSID 的推播版）。
        // 若回報的 SSID == 目標 → 視為成功(補 wifi.reconfig.succeeded 事件若漏收)。useAttr 已同步更新「目前連線」。
        const v = frameValue(frame);
        if (typeof v === 'string' && v && v === target.current) setPhase('done');
      }
    });
    const timer = setTimeout(() => {
      setError((e) => e ?? t('deviceSettings.wifiReconfigTimeout'));
      setPhase((cur) => (cur === 'switching' ? 'failed' : cur));
    }, RESULT_TIMEOUT_MS);
    return () => {
      off();
      clearTimeout(timer);
    };
  }, [phase, deviceId, t]);

  const reset = useCallback(() => {
    setPhase('idle');
    setError(null);
    setSsid('');
    setPsk('');
    setAps([]);
  }, []);

  // 換網成功後短暫顯示「已切換至…」,再自動收回一開始畫面(此時「目前連線」已是新 SSID)。
  useEffect(() => {
    if (phase !== 'done') return;
    const timer = setTimeout(reset, 3000);
    return () => clearTimeout(timer);
  }, [phase, reset]);

  return (
    <SectionCard
      title={t('deviceSettings.wifiReconfigTitle')}
      action={
        !localOnly && wsOpen && phase === 'idle' ? (
          // idle:「更換 Wi-Fi」小 btn-ghost 放在區塊標題那一列右側(對齊韌體「檢查更新」)。
          <button type="button" className="btn-ghost" onClick={() => void startScan()}>
            {t('deviceSettings.wifiReconfigChange')}
          </button>
        ) : undefined
      }
    >
      <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        {t('deviceSettings.wifiReconfigCurrent', {
          ssid: typeof currentSsid.value === 'string' && currentSsid.value ? currentSsid.value : '—',
        })}
      </p>

      {/* 裝置離線 → web 無法就近(BLE/熱點)換網;提示改用手機 App(不顯示換網流程)。 */}
      {localOnly ? (
        <p role="status" style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.wifiReconfigLocalOnlyNote')}
        </p>
      ) : (
        <>
      {!wsOpen && (
        <p role="status" style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.wifiReconfigOfflineNote')}
        </p>
      )}

      {phase === 'scanning' && (
        <p role="status" style={{ margin: 0 }}>
          {t('deviceSettings.wifiReconfigScanning')}
        </p>
      )}

      {phase === 'picking' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label>
            {t('deviceSettings.wifiReconfigSelectSsid')}
            <select value={ssid} onChange={(e) => setSsid(e.target.value)} aria-label={t('deviceSettings.wifiReconfigSelectSsid')}>
              <option value="">—</option>
              {aps.map((a) => (
                <option key={a.ssid} value={a.ssid}>
                  {a.ssid} ({a.rssi} dBm{a.security ? `, ${a.security}` : ''})
                </option>
              ))}
            </select>
          </label>
          <input
            type="password"
            value={psk}
            placeholder={t('deviceSettings.wifiReconfigPassword')}
            aria-label={t('deviceSettings.wifiReconfigPassword')}
            onChange={(e) => setPsk(e.target.value)}
            autoComplete="off"
          />
          <button type="submit" disabled={!ssid}>
            {t('deviceSettings.wifiReconfigSubmit')}
          </button>
          <button type="button" onClick={reset}>
            {t('common.cancel')}
          </button>
        </form>
      )}

      {phase === 'switching' && (
        <p role="status" style={{ margin: 0 }}>
          {t('deviceSettings.wifiReconfigSwitching')}
        </p>
      )}

      {phase === 'done' && (
        <p role="status" style={{ margin: 0, color: 'var(--color-semantic-success)' }}>
          {t('deviceSettings.wifiReconfigSuccess', { ssid: target.current })}
        </p>
      )}

      {phase === 'failed' && (
        <div role="alert">
          <p style={{ margin: 0, color: 'var(--color-semantic-danger)' }}>
            {error ?? t('deviceSettings.wifiReconfigFailed')}
          </p>
          <button type="button" onClick={reset}>
            {t('common.retry')}
          </button>
        </div>
      )}
        </>
      )}
    </SectionCard>
  );
}
